import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Users, History, ChevronDown, ChevronUp } from 'lucide-react';
import {
  useAdminBillingPlans,
  useCreatePlan,
  useUpdatePlan,
  useDeletePlan,
  useAdminUsers,
  useAdminSubscriptions,
} from '../../api/hooks';
import { fmtDate } from '../../lib/utils';
import { SectionHeader, Spinner } from '../../components/ui';
import { toast } from '../../store';

// ─── Plan form ──────────────────────────────────────────────────────────────────

const EMPTY_PLAN = {
  name: '',
  details: '',
  license_cost: 1,
  price: 0,
  is_active: true,
  payment_enabled: false,
  operator_id_override: '',
};

function PlanForm({ initial = EMPTY_PLAN, onSave, onCancel, saving, operators = [] }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = {
      name: form.name,
      details: form.details || null,
      license_cost: Number(form.license_cost),
      price: Number(form.price),
      is_active: form.is_active,
      payment_enabled: form.payment_enabled,
    };
    if (form.operator_id_override) body.operator_id_override = Number(form.operator_id_override);
    onSave(body);
  };

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3 text-xs">
      {operators.length > 0 && (
        <div className="col-span-2">
          <label className="block text-txt-muted mb-1">Operator (plan owner) <span className="text-bear">*</span></label>
          <select
            className="input-field w-full"
            value={form.operator_id_override}
            onChange={(e) => set('operator_id_override', e.target.value)}
            required
          >
            <option value="">Select operator…</option>
            {operators.map((u) => (
              <option key={u.id} value={u.id}>{u.name || u.email} (UID {u.id})</option>
            ))}
          </select>
        </div>
      )}
      <div className="col-span-2">
        <label className="block text-txt-muted mb-1">Plan Name</label>
        <input className="input-field w-full" value={form.name} onChange={(e) => set('name', e.target.value)} required />
      </div>
      <div className="col-span-2">
        <label className="block text-txt-muted mb-1">Details (optional)</label>
        <textarea className="input-field w-full resize-none" rows={2} value={form.details} onChange={(e) => set('details', e.target.value)} />
      </div>
      <div>
        <label className="block text-txt-muted mb-1">Number of Licenses</label>
        <input type="number" min={0} className="input-field w-full" value={form.license_cost} onChange={(e) => set('license_cost', e.target.value)} required />
        {Number(form.license_cost) > 0 && (
          <p className="text-2xs text-txt-muted mt-0.5">
            Validity: {Number(form.license_cost) * 30} days ({form.license_cost} × 30 days)
          </p>
        )}
      </div>
      <div>
        <label className="block text-txt-muted mb-1">Amount (₹)</label>
        <input type="number" min={0} className="input-field w-full" value={form.price} onChange={(e) => set('price', e.target.value)} />
      </div>
      <div className="flex items-end gap-4 pb-1">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
          <span className="text-txt-muted">Active</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.payment_enabled} onChange={(e) => set('payment_enabled', e.target.checked)} />
          <span className="text-txt-muted">Payment enabled</span>
        </label>
      </div>
      <div className="col-span-2 flex gap-2 pt-1">
        <button type="submit" className="btn-primary text-xs" disabled={saving}>
          {saving ? 'Saving…' : 'Save Plan'}
        </button>
        <button type="button" className="btn-ghost text-xs" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────────

export default function AdminSubscriptionPlansPage() {
  const { data: allUsers = [] } = useAdminUsers();
  const operators = allUsers.filter((u) => u.role === 'operator' || u.roles?.includes('operator'));
  const [selectedOpId, setSelectedOpId] = useState(null);

  useEffect(() => {
    if (!selectedOpId && operators.length > 0) setSelectedOpId(operators[0].id);
  }, [operators.length]); // eslint-disable-line

  const { data: plans = [], isLoading: plansLoading } = useAdminBillingPlans(
    selectedOpId ? { operator_id: selectedOpId } : undefined
  );

  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();
  const deletePlan = useDeletePlan();

  const { data: subscriptions = [], isLoading: subsLoading } = useAdminSubscriptions(
    selectedOpId ? { operator_id: selectedOpId } : undefined
  );

  const [showNewPlan, setShowNewPlan] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  const selectedOp = operators.find((u) => u.id === selectedOpId);

  const handleCreatePlan = async (body) => {
    try {
      await createPlan.mutateAsync(body);
      toast.success('Plan created');
      setShowNewPlan(false);
    } catch (err) {
      toast.error(err.message || 'Failed to create plan');
    }
  };

  const handleUpdatePlan = async (id, body) => {
    try {
      await updatePlan.mutateAsync({ id, body });
      toast.success('Plan updated');
      setEditingPlanId(null);
    } catch (err) {
      toast.error(err.message || 'Failed to update plan');
    }
  };

  const handleDeactivatePlan = async (id) => {
    try {
      await deletePlan.mutateAsync(id);
      toast.success('Plan deactivated');
    } catch (err) {
      toast.error(err.message || 'Failed to deactivate plan');
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <SectionHeader title="Subscription Plans" />

      {/* Operator selector */}
      <div className="flex items-center gap-3">
        <label className="text-xs text-txt-muted flex items-center gap-1">
          <Users size={13} />
          Operator
        </label>
        {operators.length === 0 ? (
          <p className="text-xs text-txt-muted">No operators found</p>
        ) : (
          <select
            className="input-field text-xs max-w-xs"
            value={selectedOpId ?? ''}
            onChange={(e) => setSelectedOpId(Number(e.target.value))}
          >
            {operators.map((u) => (
              <option key={u.id} value={u.id}>{u.name || u.email} (UID {u.id})</option>
            ))}
          </select>
        )}
      </div>

      {/* Plans section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-txt-primary">
            Plans
            {selectedOp && (
              <span className="text-txt-muted font-normal text-xs ml-2">— {selectedOp.name || selectedOp.email}</span>
            )}
          </h3>
          <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => setShowNewPlan((v) => !v)}>
            <Plus size={14} />
            New Plan
          </button>
        </div>

        {showNewPlan && (
          <div className="border border-bg-border rounded p-4 mb-4 bg-bg-surface/50">
            <PlanForm
              onSave={handleCreatePlan}
              onCancel={() => setShowNewPlan(false)}
              saving={createPlan.isPending}
              operators={operators}
            />
          </div>
        )}

        {plansLoading ? (
          <Spinner />
        ) : plans.length === 0 ? (
          <p className="text-xs text-txt-muted">No plans for this operator yet.</p>
        ) : (
          <div className="border border-bg-border rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-bg-surface border-b border-bg-border">
                <tr>
                  {['Name', 'Validity', 'Licenses', 'Price', 'Pay', 'Status', ''].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-txt-muted font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-bg-border">
                {plans.map((plan) => (
                  <>
                    <tr key={plan.id} className="hover:bg-bg-surface/40">
                      <td className="px-3 py-2 text-txt-primary font-medium">
                        {plan.name}
                        {plan.details && <div className="text-2xs text-txt-muted font-normal mt-0.5 max-w-xs truncate">{plan.details}</div>}
                        {plan.suspended && <div className="text-2xs text-bear mt-0.5">⚠ Suspended — insufficient licenses</div>}
                      </td>
                      <td className="px-3 py-2 text-txt-muted">{plan.license_cost * 30}d</td>
                      <td className="px-3 py-2 text-txt-muted">{plan.license_cost}</td>
                      <td className="px-3 py-2 text-txt-muted">₹{plan.price}</td>
                      <td className="px-3 py-2">
                        <span className={plan.payment_enabled ? 'text-bull' : 'text-txt-muted'}>
                          {plan.payment_enabled ? 'On' : 'Off'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-2xs border ${plan.is_active && !plan.suspended ? 'text-bull bg-bull/10 border-bull/30' : plan.suspended ? 'text-warn bg-warn/10 border-warn/30' : 'text-txt-muted bg-bg-surface border-bg-border'}`}>
                          {plan.suspended ? 'suspended' : plan.is_active ? 'active' : 'inactive'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <button className="text-txt-muted hover:text-txt-primary" onClick={() => setEditingPlanId(editingPlanId === plan.id ? null : plan.id)}>
                            <Edit2 size={13} />
                          </button>
                          {plan.is_active && (
                            <button className="text-txt-muted hover:text-bear" onClick={() => handleDeactivatePlan(plan.id)}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {editingPlanId === plan.id && (
                      <tr key={`edit-${plan.id}`}>
                        <td colSpan={7} className="px-3 py-3 bg-bg-surface/50">
                          <PlanForm
                            initial={{
                              name: plan.name,
                              details: plan.details ?? '',
                              license_cost: plan.license_cost,
                              price: plan.price,
                              is_active: plan.is_active,
                              payment_enabled: plan.payment_enabled,
                              operator_id_override: '',
                            }}
                            onSave={(body) => handleUpdatePlan(plan.id, body)}
                            onCancel={() => setEditingPlanId(null)}
                            saving={updatePlan.isPending}
                            operators={[]}
                          />
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Subscription Audit History ── */}
      {selectedOpId && (
        <div>
          <button
            className="flex items-center gap-2 text-sm font-medium text-txt-primary mb-3 w-full text-left"
            onClick={() => setShowAudit((v) => !v)}
          >
            <History size={15} />
            Subscription Audit History
            {showAudit ? <ChevronUp size={13} className="ml-auto" /> : <ChevronDown size={13} className="ml-auto" />}
          </button>

          {showAudit && (
            subsLoading ? <div className="text-xs text-txt-muted">Loading…</div> :
            subscriptions.length === 0 ? (
              <p className="text-xs text-txt-muted">No subscription records for this operator.</p>
            ) : (
              <div className="border border-bg-border rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-bg-surface border-b border-bg-border">
                    <tr>
                      {['Date', 'User ID', 'Plan', 'Source', 'Valid From', 'Valid Till', 'Assigned By'].map((h) => (
                        <th key={h} className="text-left px-3 py-2 text-txt-muted font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-bg-border">
                    {subscriptions.map((sub) => (
                      <tr key={sub.id} className="hover:bg-bg-surface/40">
                        <td className="px-3 py-2 font-mono text-txt-muted whitespace-nowrap">
                          {new Date(sub.created_at + 'Z').toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-txt-muted">{sub.user_id}</td>
                        <td className="px-3 py-2 text-txt-primary">{sub.plan?.name ?? '—'}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex px-1.5 py-0.5 rounded text-2xs border font-mono text-txt-muted bg-bg-surface border-bg-border">
                            {sub.source}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-txt-muted">{fmtDate(sub.valid_from)}</td>
                        <td className="px-3 py-2 font-mono text-txt-muted">{fmtDate(sub.valid_till)}</td>
                        <td className="px-3 py-2 text-txt-muted">
                          {sub.assigned_by_user_id ? `UID ${sub.assigned_by_user_id}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
